import { decodeHtml } from "../../../utilities/utils";
import { stateAbbreviations } from "./constants";

export async function getPartnersInfo({
    currentCustomer,
    customerName,
    dataMutation,
    page = 1,
    limitDomainResults = false
}) {
    let response = await dataMutation({
        variables: { id: String(currentCustomer), isMarketPlace: true, customerName, page: String(page), limitDomainResults }
    });
    let data = response.data.customOrdersMutation
    let customerToDomainMappings = JSON.parse(data.customerToDomainMappings);
    let ordersInfo = JSON.parse(data.ordersInfo);
    let networks = ordersInfo.marketplaceNetworks.map(network => ({ name: decodeHtml(network.name), value: network.code }))
    let domainsInfo = []
    for (let [k, domainObjects] of Object.entries(customerToDomainMappings.mappings)) {
        if (k) {
            domainObjects.forEach((domainInfo) => {
                if (domainInfo.overview && domainInfo.marketplaceEnabled) {
                    let domainOverview = JSON.parse(domainInfo.overview);
                    if (domainOverview.marketplace_domain_name && domainOverview.marketplace_domain_url) {
                        domainOverview.marketplace_domain_url = domainInfo.domainName;
                        domainsInfo.push(domainOverview);
                    }
                }
            });
        }
    }
    domainsInfo.sort((a, b) => a.marketplace_domain_name.localeCompare(b.marketplace_domain_name));

    return {
        networks,
        domainsInfo,
        pagination: customerToDomainMappings.pagination
    }
}

export async function requestLineItemsStats(lineItemIds, lineItemStatsMutation) {
    let statsResponse = await lineItemStatsMutation({
        variables: {
            lineItemIdsList: JSON.stringify(lineItemIds),
        },
    });

    let stats = JSON.parse(statsResponse.data?.getLineItemStatsMutation.gamApiStats);
    return stats;
}

/**
     * 
     * @param {Array<any>} geoTargets 
     * @param {Set<string>} excludedGeographies 
     * @returns 
     */

export function categorizeGeographies(geoTargets, excludedGeographies) {
    let countries = []
    let states = []
    let dmas = []
    let includedCountries = []
    let includedStates = []
    let includedDmas = []
    let excludedCountries = []
    let excludedStates = []
    let excludedDmas = []
    let isExcludingGeographies = excludedGeographies.size !== 0


    /**
     * 
     * @param {Array<any>} targets
     */

    function helper(targets) {
        if (targets.length) {
            let [first, ...rest] = targets
            if (first.countryCode) {
                let { id, countryCode, children = [] } = first
                let countryData = { id, abbr: countryCode }

                // If there are excluded geographies, go through the whole list of geographies and target the 
                // geographies that are not excluded.
                // If there are not excluded geograhpies, then just traverse the list of geographies to be added. 

                if (isExcludingGeographies) {
                    if (excludedGeographies.has(id)) {
                        excludedCountries.push(countryData)
                    } else {
                        helper(children)
                    }
                } else {
                    countries.push(first.countryCode)
                    includedCountries.push(countryData)
                }

                helper(rest)
            } else if (first.dma) {
                let { id, name, canonicalParentId } = first
                let dmaData = { name, id, parentId: canonicalParentId }

                if (isExcludingGeographies) {
                    // If either this dma or its parent (the state) has been excluded, then exclude this dma from possible targets.
                    if (excludedGeographies.has(id) || excludedGeographies.has(canonicalParentId)) {
                        excludedDmas.push(dmaData)
                    } else {
                        includedDmas.push(dmaData)
                    }
                } else {
                    includedDmas.push({ id, name })
                }

                helper(rest)
            } else {
                let { id, name, parentId: canonicalParentId, children = [] } = first
                let stateData = { id, name, abbr: stateAbbreviations[name] }

                if (isExcludingGeographies) {
                    if (excludedGeographies.has(id) || excludedGeographies.has(canonicalParentId)) {
                        excludedStates.push(stateData)
                        // question comes up: if this state has been excluded from geo-targets, should we traverse its dma children list
                        // and exclude them explicitly, too? 
                        // Counter point: the children of this state are not going to be part of the included targeting either, so why bother?
                        helper(children)
                    } else {
                        includedStates.push(stateData)
                        helper(children)
                    }
                } else {
                    includedStates.push(stateData)
                    // should we recursively traverse this state and add to the targeted geographies
                    // the DMAs belonging to this state? or is selecting the whole state enought to say that we're implicitly 
                    // targeting all its DMAs
                    helper(children)
                }

                helper(rest)
            }
        }
    }

    helper(geoTargets)

    return {
        countries,
        states,
        dmas,
        includedCountries,
        includedDmas,
        includedStates,
        excludedCountries,
        excludedDmas,
        excludedStates
    }
}

/**
 * 
 * @param {*} allGeoTargets 
 * @param {MarketplaceOrder} order 
 * @param {string} customerName 
 * @param {*} stats 
 * @returns 
 */
export function generateSspPayload(allGeoTargets, order, customerName, stats) {
    let geoTargets = {}
    let devices = ['desktop', 'tablet', 'mobile']

    if (order.excludedGeographies?.length) {
        let excludedIds = new Set(order.excludedGeographies.map(geo => geo.id))
        geoTargets = categorizeGeographies(allGeoTargets, excludedIds)
    } else {
        geoTargets = categorizeGeographies(
            order.includedGeographies?.length
                ? order.includedGeographies
                : allGeoTargets,
            new Set()
        )
    }

    if (order.selectDeviceMode) {
        let includedDevices = order.targetDevices.map(item => {
            return item.name.toLowerCase()
        })
        devices = includedDevices.join(',') || devices.join(',')
    } else {
        let excludedDevices = new Set(
            order.targetDevices.map(item => {
                return item.name.toLowerCase()
            })
        );
        devices = devices
            .filter(item => !excludedDevices.has(item))
            .join(',')
    }

    let updatePayload = {
        customer_name: customerName,
        marketplace_id: order.wpMarketplacePostId,
        startDate: order.startDate,
        endDate: order.endDate,
        devices,
        included_countries: geoTargets
            .includedCountries
            .map(item => item.abbr)
            .join(',')
            .toLowerCase(),
        excluded_countries: geoTargets
            .excludedCountries
            .map(item => item.abbr)
            .join(',')
            .toLowerCase(),
        included_states: geoTargets
            .includedStates
            .map(item => item.abbr)
            .join(',')
            .toLowerCase(),
        excluded_states: geoTargets
            .excludedStates
            .map(item => item.abbr)
            .join(',')
            .toLowerCase(),
        included_dma_name: geoTargets
            .includedDmas
            .map(item => item.name)
            .join(','),
        included_dma_code: geoTargets
            .includedDmas
            .map(item => item.id)
            .join(','),
        excluded_dma_name: geoTargets
            .excludedDmas
            .map(item => item.name)
            .join(','),
        excluded_dma_code: geoTargets
            .excludedDmas
            .map(item => item.id)
            .join(','),
        status: stats[order.lineItemId]?.status || 'ready',
        cpm: order.CPM
    }

    return updatePayload
}